import { currencySymbols } from './currencySymbols';

export interface CompanyMoneyFormat {
  locale: string;
  decimalSeparator: string;
  companyNumber: number;
  numberOfDecimals: number;
  currency: string;
}

export interface CompanyMoneyFormatWithSymbol extends CompanyMoneyFormat {
  currencySymbol: string;
}

type CurrencyCode = keyof typeof currencySymbols;

class MoneyFormatter {
  public static instance: MoneyFormatter;
  private static basePath =
    'https://www.sembo.com/d/mvc/Companies/GetCompanies';
  private formats: CompanyMoneyFormat[] = [];

  public format(amount: number, locale: string): string {
    const localeMoneyFormat = this.getLocaleMoneyFormat(locale);

    if (localeMoneyFormat === undefined) {
      return `${amount}`;
    }

    return this.getNumberFormatter(
      amount,
      localeMoneyFormat.currency,
      locale,
      localeMoneyFormat.numberOfDecimals
    );
  }

  public formatWithCurrency(
    amount: number,
    currency: string,
    locale: string
  ): string {
    const localeMoneyFormat = this.getLocaleMoneyFormat(locale);

    if (localeMoneyFormat === undefined) {
      return `${amount} ${currency}`;
    }

    return this.getNumberFormatter(
      amount,
      currency,
      locale,
      localeMoneyFormat.numberOfDecimals
    );
  }

  public getLocaleMoneyFormat(
    fromLocale: string
  ): CompanyMoneyFormatWithSymbol | undefined {
    const companyMoneyFormat = MoneyFormatter.instance.formats.find(
      (pattern) => pattern.locale === fromLocale
    );

    if (!companyMoneyFormat) {
      return;
    }

    return {
      ...companyMoneyFormat,
      currencySymbol:
        currencySymbols[companyMoneyFormat.currency as CurrencyCode],
    };
  }

  public static GetInstanceAsnyc = async (): Promise<MoneyFormatter> => {
    if (!MoneyFormatter.instance) {
      MoneyFormatter.instance = new MoneyFormatter();

      try {
        const response = await fetch(MoneyFormatter.basePath);
        const jsonResponse = await response.json();
        MoneyFormatter.instance.formats = jsonResponse;
      } catch {
        throw new Error(
          '@libs/utils > moneyFormatter.ts > MoneyFormatter > GetInstanceAsnyc > API call failed'
        );
      }

      return MoneyFormatter.instance;
    }

    return MoneyFormatter.instance;
  };

  private getNumberFormatter(
    amount: number,
    currencyCode: string,
    fullLocale: string,
    numberOfDecimals: number
  ): string {
    if (amount % 1 === 0) {
      return new Intl.NumberFormat(fullLocale, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }).format(amount);
    }

    return new Intl.NumberFormat(fullLocale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: numberOfDecimals,
    }).format(amount);
  }
}

export { MoneyFormatter };
